import React from 'react';
import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import H from '../../../components/texts/h/H';
import Badge from '../../../components/badge/Badge';
import type { indicator } from '../../../types/interfaces';
import { RISK_STATUS } from '../../../utils/Constants';
import theme from '../../../styles/theme';
import AtlanticSectionHeader from './AtlanticSectionHeader';
import {
  findNosisIndicatorByCode,
  findNosisIndicatorByLabel,
  NOSIS_TOP_SCORE_CODES,
} from './nosisTopScores';

export type NosisScoreCard = {
  title: string;
  value: string;
  status?: string | null;
};

type NosisScoresSectionProps = {
  indicators: indicator[];
  /** Prefer codes when present; fall back to legacy description labels. */
  preferCodes?: boolean;
  codes?: readonly string[];
  renderStatusBadge?: (status?: string | null) => React.ReactNode;
  /** Atlantic Paso 4: Figma banner "Scores Nosis". Off for legacy Chequeo de riesgo. */
  showSectionTitle?: boolean;
};

const DEFAULT_CARDS: Array<{
  code: string;
  title: string;
  labelFallback: string;
  defaultValue: string;
}> = [
  {
    code: 'NOSIS_001',
    title: 'Score Nosis actual',
    labelFallback: 'score nosis actual',
    defaultValue: '-',
  },
  {
    code: 'NOSIS_002',
    title: 'Score Nosis 365 dias',
    labelFallback: 'score nosis 365',
    defaultValue: '-',
  },
  {
    code: 'NOSIS_003',
    title: 'Cheques rechazados (u. 12 meses)',
    labelFallback: 'cheques rechazados',
    defaultValue: 'NO',
  },
  {
    code: 'NOSIS_005',
    title: 'Otros antecedentes neg.',
    labelFallback: 'antecedente',
    defaultValue: 'NO',
  },
];

function buildCardDefs(codes: readonly string[]): Array<(typeof DEFAULT_CARDS)[number]> {
  return codes
    .map((code) => DEFAULT_CARDS.find((d) => d.code === code))
    .filter((def): def is (typeof DEFAULT_CARDS)[number] => def != null);
}

function defaultBadge(status?: string | null) {
  if (!status) return null;
  const mapStatus = new Map([
    [RISK_STATUS.approved.label, 'success'],
    [RISK_STATUS.revision.label, 'warning'],
    [RISK_STATUS.medium_risk.label, 'warning'],
    [RISK_STATUS.high_risk.label, 'danger'],
  ]);
  const variant = mapStatus.get(status) ?? 'success';
  const text =
    Object.values(RISK_STATUS).find((item) => item.label === status)?.text ??
    'Estado desconocido';
  return (
    <Badge variant={variant} type="text" size="sm">
      {text}
    </Badge>
  );
}

function resolveCard(
  indicators: indicator[],
  preferCodes: boolean,
  def: (typeof DEFAULT_CARDS)[number]
): NosisScoreCard {
  const byCode = preferCodes
    ? findNosisIndicatorByCode(indicators, def.code)
    : undefined;
  const byLabel = findNosisIndicatorByLabel(indicators, def.labelFallback);
  const hit = byCode ?? byLabel;
  return {
    title: def.title,
    value: hit?.value || def.defaultValue,
    status: hit?.status,
  };
}

/**
 * Four Nosis KPIs. Atlantic Paso 4 uses the Figma "Scores Nosis" banner above the grid.
 */
const NosisScoresSection: React.FC<NosisScoresSectionProps> = ({
  indicators,
  preferCodes = false,
  codes = NOSIS_TOP_SCORE_CODES,
  renderStatusBadge = defaultBadge,
  showSectionTitle = false,
}) => {
  const defs = buildCardDefs(codes);
  const cards = defs.map((d) => resolveCard(indicators, preferCodes, d));

  const scoresGrid = (
    <Card
      display="grid"
      gap="20px"
      margin={{
        top: showSectionTitle ? '5' : '3',
        bottom: showSectionTitle ? '0' : '3',
      }}
      style={{
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        alignItems: 'flex-start',
        columnGap: '24px',
      }}
    >
      {cards.map((item) => (
        <Card key={item.title} display="flex" flexDirection="column" gap="8px">
          <P
            type="4"
            variant="neutral-1"
            withMargin={false}
            weight="bold"
            modifier="strong"
          >
            {item.title}
          </P>
          <Card display="flex" alignItems="center" flexWrap="wrap" gap="8px">
            <H type="3" variant="neutral-1" weight="bold" withMargin={false}>
              {item.value}
            </H>
            {renderStatusBadge(item.status)}
          </Card>
        </Card>
      ))}
    </Card>
  );

  if (showSectionTitle) {
    return (
      <div
        data-testid="nosis-scores-section"
        style={{ marginBottom: theme.spacing['spacing-5'] }}
      >
        <Card>
          <AtlanticSectionHeader
            title="Scores Nosis"
            testId="atlantic-section-header-nosis-scores"
          />
          {scoresGrid}
        </Card>
      </div>
    );
  }

  return <div data-testid="nosis-scores-section">{scoresGrid}</div>;
};

export default NosisScoresSection;
